import { isObservableArray, toJS } from 'mobx';
import { ParagraphElement, TextElement } from '@tencent/alloy-editor';
import { copy } from '@src/slide/infrastructure/module/independent/object-operation';
import getCurrentFormatTime from '@src/slide/domain/ui-model/element-modeles/property/text/utils/format-datetime';
import {
    EDITOR_DEFAULT_HEIGHT,
    EDITOR_DEFAULT_WIDTH,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-common.interface';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { getFontSize } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-level-map';
import {
    ALL_TEXT_FILL,
    BU_TYPE,
    CHARACTER_INHERIT_FILTER_KEYS,
    FONT_SCALE,
} from '../constants';
import { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { getFontCategory } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/font';
import { ITextRunStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { isSimilarityEnv } from '@src/slide/infrastructure/module/independent/url-param-env';

// 有三种类型，其中spAutoFit需要根据内部大小调整外部的，在显示上没关系，但是需要抛出事件
export function getResiziable(type) {
    switch (type) {
        default:
        case 'noAutofit': // 不管
            return false;
        case 'normAutofit': // 缩小
            return true;
        case 'spAutoFit': // 自适应
            return false;
    }
}

export function getFormatTime(run, datetime: Date) {
    if (!run) {
        return '';
    }
    const { fieldType } = run;
    const index = fieldType?.indexOf('datetime');
    if (index >= 0 && datetime) {
        const time = getCurrentFormatTime(
            fieldType.slice(index + 8),
            run.styles.lang,
            datetime,
        );
        if (time) {
            return time;
        }
    }
    return run.text;
}

export const setStylesWithElement = (styles, element) => {
    Object.entries(styles).forEach(([key, value]) => {
        element[key] = value;
        element[`origin-${key}`] = value;
    });
};

export const getDefaultRect = () => ({ left: 0, top: 0, width: EDITOR_DEFAULT_WIDTH, height: EDITOR_DEFAULT_HEIGHT });

export function isParaElementEmpty(paraEle: ParagraphElement): boolean {
    const textRuns: TextElement[] = paraEle.children;
    let textLength = 0;
    if (Array.isArray(textRuns)) {
        // eslint-disable-next-line no-restricted-syntax
        for (const run of textRuns) {
            textLength += (run.text ? run.text.length : 0);
            if (textLength > 0) return false;
        }
    }
    return textLength === 0;
}

export function isParaEmpty(para) {
    const { textRuns } = para || {};
    let textLength = 0;
    if (Array.isArray(textRuns)) {
        for (const run of textRuns) {
            textLength += (run.text ? run.text.length : 0);
            if (textLength > 0 || run.fieldType) return false; // 如果发现有filedType，文字不会为空，会展示为动态文本。
        }
    }
    return textLength === 0;
}

export function merge(raw, style) {
    if (!style) return { ...raw };

    // 项目符号相关的，进行排他性删除
    let newStyle = { ...raw };
    if (
        !expect(style.buNone).isNullOrUndefined()
        || style.buFont
        || style.buAutoNum
        || style.buBlip
        || style.character_bullet_char
        || style.buType === BU_TYPE.NONE // 手动设置去掉bu
    ) {
        CHARACTER_INHERIT_FILTER_KEYS.forEach(key => delete newStyle[key]);
    }

    newStyle = {
        ...newStyle,
        ...style,
    };

    delete newStyle.buType;

    return newStyle;
}

export function setStyleForPositionedTextInParagraphList(paragraphList, textPositions, keyword, style) {
    return paragraphList.map((paragraph, index) => setStyleForPositionedTextInParagraph(
        paragraph,
        textPositions.filter(({ paragraph }) => paragraph === index)
            .map(({ offset }) => ([offset, offset + keyword.length])), style,
    ));
}


function setStyleForPositionedTextInParagraph(paragraph, offsetList, styles) {
    // 获取textRun中需要更新的部分
    function getReplaceList(textRun, offsetList) {
        const replaceList: any[] = [];
        const end = textRun.text ? textRun.text.length : 0;
        offsetList.forEach(([s, e]) => {
            if (e < 0 || s >= end) {
                return;
            }

            const raw: any = {};

            raw.start = Math.max(0, s);
            raw.end = Math.min(end, e);

            replaceList.push(raw);
        });

        return replaceList;
    }

    let index = 0;
    let textRuns = JSON.parse(JSON.stringify(paragraph.textRuns));

    textRuns = textRuns.reduce((prev, textRun) => {
        const replaceList = getReplaceList(textRun, offsetList.map(([start, end]) => ([start - index, end - index])));
        index += textRun.text?.length || 0;
        if (textRun.styles.href?.iconMode) {
            const newTextRuns = [{
                ...textRun,
                styles: {
                    ...textRun.styles,
                    ...styles,
                },
            }];
            return prev.concat(newTextRuns);
        }
        let newTextRuns: any[] = [];

        if (textRun.text && replaceList.length) {
            let start = 0;
            let end;

            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < replaceList.length; i++) {
                end = replaceList[i].start;
                newTextRuns.push({
                    ...textRun,
                    text: textRun.text.slice(start, end),
                });
                start = replaceList[i].end;
                newTextRuns.push({
                    styles: {
                        ...textRun.styles,
                        ...styles,
                    },
                    text: textRun.text.slice(end, start),
                });
            }

            newTextRuns.push({
                ...textRun,
                text: textRun.text.slice(start),
            });
            newTextRuns = newTextRuns.filter(({ text }) => text.length);
        }

        return (newTextRuns.length && prev.concat(newTextRuns)) || prev.concat([textRun]);
    }, []);

    return {
        ...paragraph,
        textRuns,
    };
}

/**
 * textRuns打平的文字长度
 * @param {*} textRuns textRuns
 */
export function getTextLength(paragraph) {
    if (!(paragraph?.textRuns)) return 0;
    return paragraph.textRuns.reduce((prev, { text = '' } = {}) => prev + ((text?.length) || 0), 0);
}

/**
 * 清除paragraphList中start->end的部分
 * @param {*} paragraph
 * @param {*} start
 * @param {*} end
 */
export function rmSelectedText(paragraph, start, end) {
    let offset = 0;
    const p = JSON.parse(JSON.stringify(paragraph));
    const newTextRuns: any[] = [];
    const removedTextRuns: any[] = [];
    p.textRuns.forEach((textRun) => {
        const l = textRun?.text?.length || 0;
        if (offset + l <= start || offset >= end) {
            offset += l;
            newTextRuns.push(textRun);
            return;
        }
        if (offset >= start && offset + l <= end) {
            offset += l;
            removedTextRuns.push(textRun);
            return;
        }

        // 需要切割
        const s = Math.max(offset, start);
        const e = Math.min(end, offset + l);
        removedTextRuns.push({
            ...textRun,
            text: textRun.text.slice(s - offset, e - offset),
        });
        if (s > offset) {
            newTextRuns.push({
                ...textRun,
                text: textRun.text.slice(0, s - offset),
            });
        }
        if (e < offset + l) {
            newTextRuns.push({
                ...textRun,
                text: textRun.text.slice(e - offset, l),
            });
        }
        offset += l;
    });

    return {
        newTextRuns,
        removedTextRuns,
    };
}

export function textWithFill(style) {
    return style && ALL_TEXT_FILL.some(key => style[key]);
}
export function textRunStyleMerge(raw, ...styles) {
    function merge(raw, style) {
        // fonts要自己继承
        let fonts = null;
        if ((raw?.fonts) || (style?.fonts)) {
            fonts = {
                ...raw?.fonts,
                ...style?.fonts,
            };
        }
        if (raw && textWithFill(style)) {
            ALL_TEXT_FILL.map(key => delete raw[key]);
        }

        const n = {
            ...raw,
            ...style,
        };
        if (fonts) {
            n.fonts = fonts;
        }

        return n;
    }

    return styles.reduce((prev, style) => merge(prev, style), raw);
}


function updateFontSizeAfterResize(styles, resizeLevel) {
    // 根据自缩排等级更新字体大小
    if (!expect(resizeLevel).isNullOrUndefined()) {
        // eslint-disable-next-line no-param-reassign
        styles.fontSize = getFontSize(styles.displayFontSize, resizeLevel);
    }
}

/**
 * 根据继承关系，获取textrun上应该挂载的style
 * TODO 另外会给fontSize加默认值...这个太蠢了
 * @param {Object} paraStyle para上的style
 * @returns {Object} para上挂载的style
 */
// eslint-disable-next-line complexity, max-len
export function combineTextrunStyle({ textRunStyle, text = '', resizeLevel = undefined, noRawData = false }) {
    // TODO fonts会转不回去，临时处理，重构之后会统一处理
    const styles = textRunStyleMerge({}, textRunStyle);

    if (!noRawData) {
        // 带raw前缀的是非继承值的key，转换回来的时候根据其判断哪些是需要转换的
        // 不使用[]的原因是slate的mark修改设置数组不好处理
        textRunStyle && Object.keys(textRunStyle).forEach((key) => {
            styles[`raw-${key}`] = true;
        });
    }

    if (!styles.fontSize) {
        styles.fontSize = 18;
    }

    styles.rawFontSize = styles.fontSize;
    styles.displayFontSize = FONT_SCALE * styles.rawFontSize;
    // 自缩排字体大小在从 editor 数据转为 paragraphList 时需要用 displayFontSize 作为落盘的数据，所以这里要加上 raw- 前缀，避免转换回来时丢失
    if (textRunStyle?.['raw-fontSize']) {
        styles['raw-displayFontSize'] = true;
    }
    styles.fontSize = styles.displayFontSize;

    updateFontSizeAfterResize(styles, resizeLevel);
    styles.fontFamily = getFontFamily(text, styles.fonts, styles.lang);
    return styles;
}

/**
 * 根据文字内容获取最终生效的字体
 * 如果文字类型对应的字体没设置，按照旧逻辑，默认使用 ea 对应的字体
 */
function getFontFamily(text: string, fonts: ITextRunStyle['fonts'], lang: 'latin' | 'ea') {
    if (!fonts) {
        return;
    }
    const fontCategory = getFontCategory(text, lang);
    return fonts[fontCategory] || fonts.ea;
}

export function judgeTextrunsIsEmpty(textruns) {
    if (!textruns) return true;
    const ts = Array.from(textruns);
    if (!ts.length) return true;

    return false;
}


export const reduceCallback = textRun => (prev, curr) => {
    if (expect(textRun.styles[curr]).isNullOrUndefined()) return prev;
    return {
        ...prev,
        [curr]: textRun.styles[curr],
    };
};

// src/framework/canvas-mode/canvas-render/components/text-editor/utils/common-utils.ts

export function isEditorEmpty(paraList) {
    if (isSimilarityEnv()) {
        const hasNoText = paraList.length === 1 && paraList[0]?.textRuns?.length === 0;
        // 和office跑版对比时 不做此性能优化 要和office对齐(只有一个textRun且为空的场景，office也认为不是文本)
        return hasNoText;
    }
    let isEmpty = true;
    if (paraList) {
        for (let i = 0; i < paraList.length; i++) {
            const paraEmpty = isParaEmpty(paraList[i]);
            if (!paraEmpty) {
                isEmpty = false;
                break;
            }
        }
    }
    return isEmpty;
}

/**
 * 获取段落中所有文本
 * @param paraList
 */
export function getTextInParagraphList(paraList: IParagraph[] = []): string {
    let content = '';
    if (paraList) {
        for (let i = 0; i < paraList.length; i++) {
            const para = paraList[i];
            const { textRuns } = para || {};
            if (Array.isArray(textRuns)) {
                for (const run of textRuns) {
                    content += run.text || '';
                }
            }
        }
    }
    return content;
}

// 获取原始数据。这里会拷贝一份，否则会改变原始数据
// @props 如果没有props，认为是从外部进来的
export function getParagraphList(props, hasProps) {
    let p = props.paragraphList;
    if (isObservableArray(p)) {
        p = toJS(p);
    } else {
        p = copy(p, true);
    }

    // 如果是空Editor，添加一个空的textRun，否则空文本会没有样式
    if (!hasProps) { // 认为是从外部进来的
        const editorEmpty = isEditorEmpty(p);
        if (editorEmpty) {
            if (Array.isArray(p) && p.length > 0) {
                p.forEach((paragraph) => {
                    if (!paragraph.textRuns?.length) {
                        // eslint-disable-next-line no-param-reassign
                        paragraph.textRuns = [{ text: '', styles: paragraph.endParaRPr || {} }];
                    }
                });
            } else {
                p = [{
                    paragraphProps: {},
                    textRuns: [{ text: '', styles: {} }],
                }];
            }
        }  else {
            // 空行，也需要设置上一个占位textRun，否则无法设置样式
            Array.isArray(p) && p.forEach((paragraph) => {
                if (!paragraph.textRuns?.length) {
                    // eslint-disable-next-line no-param-reassign
                    paragraph.textRuns = [{ text: '', styles: paragraph.endParaRPr || {} }];
                }
            });
        }
    }

    return p;
}

/**
 * 获取指定 url 的 query 参数值
 * @param url
 * @returns
 */
export function getUrlQuery(url: string): {
    [key: string]: string
} {
    const [, search] = url.split('?');
    if (search) {
        const params = search.split('&');
        return params.reduce((queryObj, item) => {
            const [key, value] = item.split('=');
            return {
                ...queryObj,
                [key]: value,
            };
        }, {});
    }
    return {};
}

/**
 * 修改段落字体数据信息
 * @param paragraphList
 * @param fontFamily
 * @returns {*}
 */
export function updateFont(paragraphList, fontFamily) {
    return paragraphList.map((paraItem) => {
        const para = paraItem;
        para.textRuns = para?.textRuns.map((run) => {
            const textRun = run;
            textRun.styles = { ...textRun.styles };
            textRun.styles.fonts = { ...textRun.styles.fonts };
            textRun.styles.fontFamily = fontFamily;
            textRun.styles['raw-fontFamily'] = true; // 加上raw-标志，用以提交sdk时区分继承属性
            textRun.styles.fonts = {
                ea: fontFamily,
                latin: fontFamily,
            };
            textRun.styles['raw-fonts'] = true; // 加上raw-标志，用以提交sdk时区分继承属性
            return textRun;
        });
        if (para.paragraphProps?.defaultRunProps) {
            const defaultRunProps = para.paragraphProps?.defaultRunProps;
            defaultRunProps.fonts = defaultRunProps.fonts || {};
            if (defaultRunProps.styles?.lang) {
                defaultRunProps.fonts[defaultRunProps.styles?.lang] = fontFamily;
            } else {
                defaultRunProps.fonts.ea = fontFamily;
                defaultRunProps.fonts.latin = fontFamily;
            }
        }
        return para;
    });
}
